import React, { useEffect, useState } from "react";
import { useSortBy, useTable } from "react-table";
import { SetInfo } from "../utils/tableHelpers";

import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';

export function CreateTableWithHeaderDragSummary({ dataList = [], dataHeader = [], dataAccessor = [], currentPage, setCurrentPage, editHandleClick, deleteHandleClick, setSelected, employees, handleDragStart, filteredHeaderForSummary }) {
  // const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [updatedDataHeader, setUpdatedDataHeader] = useState(dataHeader);
  const [updatedDataAccessor, setUpdatedDataAccessor] = useState(dataAccessor);

  useEffect(() => {
    let itemsToRemove = [];

    if (filteredHeaderForSummary && filteredHeaderForSummary.length > 0) {
      itemsToRemove = filteredHeaderForSummary.map(item => item.textContent);
    }

    const removedItemIndexes = [];
    const newUpdatedDataHeader = dataHeader.filter((item, index) => {
      if (itemsToRemove.includes(item)) {
        removedItemIndexes.push(index);
        return false; // Exclude the item from the filtered array
      }
      return true; // Include the item in the filtered array
    });
    const newDataAccessor = dataAccessor.filter((_, index) => !removedItemIndexes.includes(index));
    setUpdatedDataHeader(newUpdatedDataHeader);
    setUpdatedDataAccessor(newDataAccessor);
  }, [dataHeader, dataAccessor, filteredHeaderForSummary]);

  const columns = React.useMemo(() =>
    updatedDataHeader.map((header, index) => ({
      Header: header,
      accessor: updatedDataAccessor[index]
    })),
    [updatedDataHeader, updatedDataAccessor]
  );

  const [sortHeaderValue, setSortHeaderValue] = useState({ id: "", desc: false, isDefault: true });

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    setSortBy
  } = useTable(
    {
      columns,
      data: dataList,
      initialState: {
        sortBy: sortHeaderValue.isDefault
          ? []  // No sorting if it's default
          : [{ id: sortHeaderValue.id, desc: sortHeaderValue.desc }] // Apply sorting if not default
      }
    },
    useSortBy
  );

  const indexOfFirstItem = (currentPage - 1) * itemsPerPage;
  const indexOfLastItem = Math.min(currentPage * itemsPerPage, rows.length);
  const currentItems = rows.slice(indexOfFirstItem, indexOfLastItem);

  const totalPages = Math.ceil(rows.length / itemsPerPage);
  const isLastPage = currentPage === totalPages;

  const paginate = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  //Sorting Feature
  const sortState = [
    { id: 0, state: "default", textValue: "" },
    { id: 1, state: "ascending", textValue: "↑" },
    { id: 2, state: "descending", textValue: "↓" }
  ];

  const headerClick = (column) => {
    if (sortHeaderValue.id !== column.id) {
      // New column selected, start with ascending order
      setSortHeaderValue({ id: column.id, desc: false, isDefault: false });
      setSortBy([{ id: column.id, desc: false }]);
    } else {
      // Toggle between sorting states (default -> ascending -> descending -> default)
      if (sortHeaderValue.isDefault) {
        setSortHeaderValue({ id: column.id, desc: false, isDefault: false }); // Ascending
        setSortBy([{ id: column.id, desc: false }]);
      } else if (!sortHeaderValue.desc) {
        setSortHeaderValue({ id: column.id, desc: true, isDefault: false }); // Descending
        setSortBy([{ id: column.id, desc: true }]);
      } else {
        setSortHeaderValue({ id: "", desc: false, isDefault: true }); // Default (no sorting)
        setSortBy([]); // Clear sorting
      }
    }
  };

  // Ensure sorting is reapplied on dataList reload
  useEffect(() => {
    if (!sortHeaderValue.isDefault && sortHeaderValue.id) {
      setSortBy([{ id: sortHeaderValue.id, desc: sortHeaderValue.desc }]);
    }
  }, [dataList, setSortBy, sortHeaderValue]);

  return (
    <>
      <div className='scrollable-table'>
        <table {...getTableProps()} className='border-collapse'>
          <thead>
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  <th {...column.getHeaderProps()}
                    className='draggable-header sortable-header'
                    draggable
                    onDragStart={(e) => handleDragStart(e, column.render("Header"))}
                    onClick={() => headerClick(column)}
                  >
                    {`${column.render("Header")} ${sortHeaderValue.id === column.id
                      ? sortState[sortHeaderValue.isDefault ? 0 : sortHeaderValue.desc ? 2 : 1].textValue
                      : ""
                      }`}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {dataList.length <= 0 &&
              <tr>
                <td colSpan={columns.length} style={{ textAlign: "center" }}>
                  No data to display
                </td>
              </tr>
            }
            {currentItems.map((row) => {
              prepareRow(row)
              return (
                <tr key={row.original.id}
                  {...row.getRowProps()}
                >
                  {row.cells.map((cell) => (
                    <SetInfo header={cell.render("Cell").props.column.Header}
                      _cell={cell}
                      editHandleClick={editHandleClick}
                      deleteHandleClick={deleteHandleClick}
                      employees={employees}
                    />
                  ))}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      {rows.length > itemsPerPage ? (
        <>
          <div className='table-page row' style={{ alignItems: "center", justifyContent: "end", padding: "20px 0 0 0" }}>
            <button className='prev row' onClick={() => paginate(1)} disabled={currentPage === 1}
              style={{ visibility: (currentPage === 1) ? 'hidden' : 'visible', marginRight: "5px", height: "26px" }}>
              &lt;&lt;
            </button>
            <button className='prev row' onClick={() => paginate(currentPage - 1)} disabled={currentPage === 1}
              style={{ visibility: (currentPage === 1) ? 'hidden' : 'visible', marginRight: "5px", height: "26px" }}>
              <NavigateBeforeIcon style={{ marginRight: "5px" }} /> PREVIOUS
            </button>
            <button className='next row' onClick={() => paginate(currentPage + 1)} disabled={indexOfLastItem >= rows.length}
              style={{ visibility: (rows.length > itemsPerPage && !isLastPage) ? 'visible' : 'hidden', marginRight: "5px", height: "26px" }}>
              NEXT <NavigateNextIcon style={{ marginLeft: "5px" }} />
            </button>
            <button className='next row' onClick={() => paginate(totalPages)} disabled={indexOfLastItem >= rows.length}
              style={{ visibility: (rows.length > itemsPerPage && !isLastPage) ? 'visible' : 'hidden', height: "26px" }}>
              &gt;&gt;
            </button>
          </div>
          <div className='table-page row' style={{ alignItems: "center", justifyContent: "center", padding: "20px 0 0 0" }}>
            {`${currentPage}/${totalPages}`}
          </div>
        </>
      ) :
        <></>}
    </>
  )
}